import dayjs, { type Dayjs } from "dayjs"
import moment, { type Moment } from "moment"

export const STANDARD_DATE_FORMAT = "M/D/YY"
export const STANDARD_DAY_FORMAT = "dddd"
export const STANDARD_TIME_FORMAT = "h:mm a"
export const STANDARD_DATE_TIME_FORMAT = `${STANDARD_DATE_FORMAT} ${STANDARD_TIME_FORMAT}`
// Student DOBs
export const STUDENT_DOB_FORMAT = "MMYY"

// What a receipt shows when a DOB can't be trusted. Admin surfaces should keep
// rendering "Invalid Date" — that's a useful signal that a record needs
// fixing. A parent-facing email is not the place to surface it.
export const UNKNOWN_DOB = "Unknown"

// Any non-digit run works as a separator, so stored "09-07-19" and
// "09//07/2019" normalize; "0614" and "01/2011" do not.
const DOB_SHAPE = /^\d{1,2}\D+\d{1,2}\D+(\d{2}|\d{4})$/

/**
 * A DOB is a calendar day, never an instant. Returns null instead of guessing
 * — `dayjs(undefined)` is *now*, and a partial value like "0614" or "222222"
 * parses to a confident, invented birthday. Mirror of `formatDob` in
 * `server/serverDateHelpers.ts`; keep the two in sync.
 */
export const formatDob = (
  dob: string | undefined | null,
  format: string = STANDARD_DATE_FORMAT
): string | null => {
  const value = dob?.trim()
  if (!value || !DOB_SHAPE.test(value)) return null

  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format(format) : null
}
// Class session list
export const STANDARD_SESSION_DATE_FORMAT = "M/D"
// Teacher calendar
export const SPELLED_OUT_DATE_FORMAT = "ddd, MMMM D, YYYY"

export const EXTENDED_FILE_DATE_FORMAT = "M-D-YY h:mm a"
export const STANDARD_FILE_DATE_FORMAT = "M-D-YY"

// Disable before 8am and after 7pm in Antd TimePicker
export const disabledHours = [0, 1, 2, 3, 4, 5, 6, 7, 20, 21, 22, 23]

const MOCK_NOW = import.meta.env.VITE_MOCK_NOW
export const getNow = () => {
  return dayjs(MOCK_NOW)
}

export const isToday = (date: Dayjs, now: Dayjs) => {
  return date.isSame(now, "day")
}

export const isInFuture = (date: Dayjs, now: Dayjs) => {
  return date.isAfter(now)
}

export const isTodayOrFuture = (date: Dayjs, now: Dayjs) => {
  return isToday(date, now) || isInFuture(date, now)
}

export const setMomentTimeToZero = (date: Moment): Moment => {
  return date.startOf("day")
}

export const setDayjsTimeToZero = (date: Dayjs): Dayjs => {
  if (date == null || !dayjs.isDayjs(date) || !date.isValid())
    throw new Error(`setDayjsTimeToZero: Invalid date: ${JSON.stringify(date)}`)

  return date.startOf("day")
}

export const setTimeFromString = (
  dateStr: string | undefined | null,
  timeStr: string | undefined | null
): Dayjs | null => {
  if (dateStr == null || timeStr == null || !dayjs(dateStr).isValid())
    return null

  const [time, meridian] = timeStr.split(" ")
  const [hours, minutes] = time.split(":")
  let hour = parseInt(hours)

  // Convert to 24 hour format if PM
  if (meridian.toLowerCase() === "pm" && hour !== 12) hour += 12
  // Convert 12 AM to 0
  if (meridian.toLowerCase() === "am" && hour === 12) hour = 0

  const result = dayjs(dateStr)
    .set("hour", hour)
    .set("minute", parseInt(minutes))

  if (!result.isValid()) return null

  return result
}

const MISSING_DATE = "⚠️"

export const formatISOString = (
  isoString: string | undefined | null
): string => {
  if (!isoString) return MISSING_DATE
  return moment(isoString).format(STANDARD_DATE_FORMAT)
}

export const formatISOStringWithTime = (
  isoString: string | undefined | null
): string => {
  if (!isoString) return MISSING_DATE
  return moment(isoString).format(STANDARD_DATE_TIME_FORMAT)
}

export const formatISOStringToTime = (
  isoString: string | undefined | null
): string => {
  if (!isoString) return MISSING_DATE
  return moment(isoString).format(STANDARD_TIME_FORMAT)
}

// Locale-based time formatting helper
export const formatTimeLocale = (date: Dayjs): string => {
  if (!date?.isValid()) return MISSING_DATE
  return date.format(STANDARD_TIME_FORMAT).toLowerCase()
}

export const findThanksgivingWeek = (year: number): string[] => {
  const nov1 = new Date(year, 10, 1)
  const thanksgivingDay = 28 - ((nov1.getDay() + 2) % 7)

  const thanksgivingWeek = []

  for (let i = 0; i < 7; i++) {
    // Weeks start on Monday
    const day = thanksgivingDay - 3 + i
    const date = moment(new Date(year, 10, day)).format(STANDARD_DATE_FORMAT)

    thanksgivingWeek.push(date)
  }

  // console.log('thanksgiving week', thanksgivingWeek)

  return thanksgivingWeek
}
